import { Rate } from 'screens/company-user/application-form/types';
import { isNil, roundInternational } from 'utils/common';

import { CurrencyEnum, Job, StriiveJobSourceEnum } from '@libs/graphql-types';
import { formatCurrency3 } from '@libs/helpers/format/currency';

export const getRateForJob = (job: Job, payType?: string) => {
  const rateType = job.striive_job?.rate_type;
  const rates: Rate[] = JSON.parse(job.striive_job?.rates || '[]');

  if (rateType) {
    return rates?.[rateType];
  }

  const otRates = rates.filter((i) => i.class === 'ST');

  const rate =
    otRates.find((rate: Rate) => rate.payType === payType) || otRates[0];

  return rate;
};

const formatRate = (
  {
    number,
    currency = 'USD',
  }: {
    number?: number;
    currency?: CurrencyEnum | null;
  } = {
    currency: CurrencyEnum.Usd,
  },
) => {
  return !isNil(number)
    ? formatCurrency3(number, { currency: currency || CurrencyEnum.Usd })
    : 'N/A';
};

const extractRates = (job: Job): Rate[] => {
  try {
    return JSON.parse(job.striive_job?.rates || '[]') as Rate[];
  } catch (error) {
    return [];
  }
};

export const getPayTypesFromJob = (job: Job, rateClass: 'ST' | 'OT' | 'DT') => {
  const rates = extractRates(job);
  return rates
    .filter((rate) => rate.class === rateClass)
    .map((rate) => rate.payType)
    .filter((payType) => payType !== null);
};

export const getRateByClass = (
  job: Job,
  rateClass: 'ST' | 'OT' | 'DT',
  options?: { payType: string },
) => {
  const rates = extractRates(job);
  const rate = rates.find((rate: Rate) => {
    const classMatches = rate.class === rateClass;
    if (options?.payType) {
      return classMatches && rate.payType === options.payType;
    }
    return classMatches;
  });

  if (!rate) {
    return {
      billAmount: 'N/A',
      payAmount: 'N/A',
      factor: 'N/A',
    };
  }

  return {
    billAmount: rate?.billAmount,
    payAmount: rate?.payAmount,

    billAmountFormated: formatRate({
      number: rate?.billAmount,
      currency: rate?.billAmountCurrency as CurrencyEnum,
    }),
    payAmountFormated: formatRate({
      number: rate?.payAmount,
      currency: rate?.payAmountCurrency as CurrencyEnum,
    }),
    factor: rate?.factor,
  };
};

const ST_BILL_RATE_FACTOR: Record<StriiveJobSourceEnum, number> = {
  [StriiveJobSourceEnum.Maximus]: 1.2987,
  [StriiveJobSourceEnum.Bayer]: 1.38,

  [StriiveJobSourceEnum.Atos]: 1.1,
  [StriiveJobSourceEnum.Wtw]: 1.1,
  [StriiveJobSourceEnum.Between]: 1.1,
  [StriiveJobSourceEnum.Capgemini]: 1.1,
  [StriiveJobSourceEnum.FastFlex]: 1.1,
  [StriiveJobSourceEnum.FastFlexSourcing]: 1.1,
  [StriiveJobSourceEnum.Group]: 1.1,
  [StriiveJobSourceEnum.HeadFirst]: 1.1,
  [StriiveJobSourceEnum.HeadFirstBe]: 1.1,
  [StriiveJobSourceEnum.Jenrick]: 1.1,
  [StriiveJobSourceEnum.Hfrb]: 1.1,
  [StriiveJobSourceEnum.Myler]: 1.1,
  [StriiveJobSourceEnum.Rabobank]: 1.1,
  [StriiveJobSourceEnum.Shell]: 1.1,
  [StriiveJobSourceEnum.Source]: 1.1,
  [StriiveJobSourceEnum.StackOps]: 1.1,
  [StriiveJobSourceEnum.StaffingMs]: 1.1,
  [StriiveJobSourceEnum.StarApple]: 1.1,
  [StriiveJobSourceEnum.Sterksen]: 1.1,
  [StriiveJobSourceEnum.Striive]: 1.1,
  [StriiveJobSourceEnum.YellowFriday]: 1.1,
  [StriiveJobSourceEnum.Netive]: 1.1,
};

const getSTBillRateFactor = (job: Job) => {
  const jobSource = job.striive_job?.source || StriiveJobSourceEnum.Maximus;
  return (
    ST_BILL_RATE_FACTOR[jobSource] ||
    ST_BILL_RATE_FACTOR[StriiveJobSourceEnum.Maximus]
  );
};

export const calculateBillRate = (standardRate: number, job: Job) => {
  const billRate = standardRate * getSTBillRateFactor(job);
  return roundInternational(billRate);
};
